import * as React from "react";
import { PostalAddress } from "@testout/testout-commerce/models/commerce/PostalAddress";
import "./PostalAddressCtl.scss";
import SelectCtl from "./input/SelectCtl";
import { Country, ICountryLocale } from "@testout/testout-commerce/models/Country";


interface PostalAddressProps {
    disabled?: boolean;
    postal: PostalAddress;
    onChange?: (postal: PostalAddress) => void;
    defaultCountryCode: string;
    hideExtraLines?: boolean;
}

interface PostalAddressState {
    locale: ICountryLocale;
    showExtraLines: boolean;
}

export default class PostalAddressCtl extends React.PureComponent<PostalAddressProps, PostalAddressState> {
    constructor(props: PostalAddressProps) {
        super(props);
        this.state = { 
            showExtraLines: !props.hideExtraLines && (!!props.postal.line2 || !!props.postal.line3), 
            locale: new Country(props.postal?.countryCode || props.defaultCountryCode).locale
        };
    }

    static getDerivedStateFromProps(props: PostalAddressProps, state: PostalAddressState) {
        const showExtraLines = !!props.postal.line2 || !!props.postal.line3 || (state.showExtraLines && !props.disabled && !props.hideExtraLines);
        if (props.postal.countryCode !== state.locale.country.countryCode)
            return { showExtraLines: showExtraLines, locale: new Country(props.postal.countryCode ?? props.defaultCountryCode).locale };
        else 
            return { showExtraLines: showExtraLines };
    }

    onLine1Change = (e: React.ChangeEvent<HTMLInputElement>) => {
        let postal = new PostalAddress(this.props.postal);
        postal.line1 = e.target.value;
        this.props.onChange?.(postal);
    }

    onLine2Change = (e: React.ChangeEvent<HTMLInputElement>) => {
        let postal = new PostalAddress(this.props.postal);
        postal.line2 = e.target.value;
        this.props.onChange?.(postal);
    }

    onLine3Change = (e: React.ChangeEvent<HTMLInputElement>) => {
        let postal = new PostalAddress(this.props.postal);
        postal.line3 = e.target.value;
        this.props.onChange?.(postal);
    }

    onShowExtraLinesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ showExtraLines: e.target.checked && !this.props.hideExtraLines });
    };

    onCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let postal = new PostalAddress(this.props.postal);
        postal.city = e.target.value;
        this.props.onChange?.(postal);
    }

    onStateOrProvinceSelectChange = (value: string) => {
        let postal = new PostalAddress(this.props.postal);
        postal.stateOrProvince = value;
        this.props.onChange?.(postal);
    }
    onStateOrProvinceInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let postal = new PostalAddress(this.props.postal);
        postal.stateOrProvince = e.target.value;
        this.props.onChange?.(postal);
    }

    onPostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let postal = new PostalAddress(this.props.postal);
        postal.postalCode = e.target.value;
        this.props.onChange?.(postal);
    }

    onCountryChange = (value: string) => {
        const locale = new Country(value).locale;
        let postal = new PostalAddress(this.props.postal);
        if (locale.position.indexOf(0) < 0) postal.city = "";
        if (locale.position.indexOf(1) < 0) postal.stateOrProvince = "";
        if (locale.position.indexOf(2) < 0) postal.postalCode = "";
        postal.countryCode = value;
        this.setState({ locale: locale });
        this.props.onChange?.(postal);
    }

    renderLocale() {
        const fields: JSX.Element[] = [];
        for (let f = 0; f < this.state.locale.position.length; f++)
            if (this.state.locale.position[f] === 0)
                fields.push(<input type="text" key="city"
                                   style={{ width: `${this.state.locale.width[f]}%` }}
                                   placeholder={this.state.locale.name[f]}
                                   disabled={this.props.disabled} className={`input${this.props.disabled ? " disabled" : ""}`}
                                   value={this.props.postal?.city ?? ""}
                                   onChange={this.onCityChange}/>);

            else if (this.state.locale.position[f] === 1) {
                if (this.state.locale.country.provinces && this.state.locale.country.provinces.length > 0)
                    fields.push(<SelectCtl className="state-province" key="stateorprovince"
                                           style={{ width: `${this.state.locale.width[f]}%` }}
                                           placeholder={this.state.locale.name[f]}
                                           disabled={ this.props.disabled }
                                           value={this.props.postal?.stateOrProvince ?? ""}
                                           onChange={this.onStateOrProvinceSelectChange}
                                           items={this.state.locale.country.provinces.map((province) =>
                                               ({ value: province.stateOrProvince, name: province.name }))}/>);
                else
                    fields.push(<input type="text" className={`state-province${this.props.disabled ? " disabled" : ""}`} key="stateorprovince"
                                       style={{ width: `${this.state.locale.width[f]}%` }}
                                       placeholder={this.state.locale.name[f]}
                                       disabled={this.props.disabled}
                                       value={this.props.postal?.stateOrProvince ?? ""}
                                       onChange={this.onStateOrProvinceInputChange}/>);

            } else if (this.state.locale.position[f] === 2)
                fields.push(<input type="text" className={`postal-code${this.props.disabled ? " disabled" : ""}`} key="postalcode"
                                   style={{ width: `${this.state.locale.width[f]}%` }}
                                   placeholder={this.state.locale.name[f]}
                                   disabled={this.props.disabled}
                                   value={this.props.postal?.postalCode ?? ""}
                                   onChange={this.onPostalCodeChange}/>);
        return fields;
    }

    render() {
        return <div className="postal">
                   <div className="street">
                       <input type="text"
                              placeholder="street"
                              disabled={this.props.disabled}className={`input${this.props.disabled ? " disabled" : ""}`}
                              value={this.props.postal?.line1 ?? ""}
                              onChange={ this.onLine1Change}/>
                       {!this.props.hideExtraLines &&
                       <><input type="checkbox" title="Show extra lines"
                              disabled={this.props.disabled || this.props.hideExtraLines}
                              checked={this.state.showExtraLines}
                              onChange={this.onShowExtraLinesChange}/>
                       <input type="text"
                              placeholder="street 2"
                              disabled={this.props.disabled} className={`input${this.props.disabled ? " disabled" : ""}`}
                              value={this.props.postal?.line2 ?? ""}
                              onChange={this.onLine2Change}/>
                       <input type="text"
                              placeholder="street 3"
                              disabled={this.props.disabled} className={`input${this.props.disabled ? " disabled" : ""}`}
                              value={this.props.postal?.line3 ?? ""}
                              onChange={this.onLine3Change}/>
                       </>}
                   </div>
                   {this.renderLocale()}
                   <SelectCtl placeholder="country"
                              disabled={this.props.disabled} className={`input${this.props.disabled ? " disabled" : ""}`}
                              value={this.props.postal?.countryCode ?? this.props.defaultCountryCode}
                              onChange={this.onCountryChange}
                              items={Country.byName.map((country) =>
                                  ({ value: country.countryCode, name: country.name }))}/>
               </div>;
    }
}